/** Persist affiliate referral attribution across the site (30-day window). */

const STORAGE_KEY = 'maktech_affiliate_ref_v1';
const CLICK_DEDUP_KEY = 'maktech_affiliate_ref_click_v1';
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredRef {
  code: string;
  /** Landing path when ref was captured (e.g. /course/python) — informational only. */
  landedPath: string;
  capturedAt: number;
  expiresAt: number;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function read(): StoredRef | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRef;
    if (!parsed?.code || !parsed.expiresAt) return null;
    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(entry: StoredRef): void {
  if (!isBrowser()) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
}

/** Normalize affiliate codes from the URL (e.g. MAK-NA01). */
export function normalizeAffiliateCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = String(raw).trim().toUpperCase();
  if (!code || code.length > 32) return null;
  if (!/^[A-Z0-9][A-Z0-9_-]*$/.test(code)) return null;
  return code;
}

/**
 * Capture `?ref=` from a landing visit. Overwrites any previous attribution
 * (last-click wins). Attribution is site-wide — any later purchase in the
 * window credits this affiliate, not only the course they landed on.
 * Returns `{ code, isNewClick }` — isNewClick is true when this visit should count as a click.
 */
export function captureAffiliateRef(
  rawCode: string,
  landedPath = '/',
): { code: string; isNewClick: boolean } | null {
  const code = normalizeAffiliateCode(rawCode);
  if (!code) return null;
  const now = Date.now();
  const prev = read();
  write({
    code,
    landedPath: landedPath || '/',
    capturedAt: now,
    expiresAt: now + WINDOW_MS,
  });

  // Deduplicate React Strict Mode + same-tab refreshes within a few seconds.
  let isNewClick = !prev || prev.code !== code;
  if (isBrowser()) {
    try {
      const last = sessionStorage.getItem(CLICK_DEDUP_KEY);
      if (last === code) isNewClick = false;
      else sessionStorage.setItem(CLICK_DEDUP_KEY, code);
    } catch {
      /* ignore */
    }
  }

  return { code, isNewClick };
}

/** Active affiliate code for checkout, or null if expired / missing. */
export function getAffiliateRef(): string | null {
  return read()?.code ?? null;
}

export function getAffiliateAttribution(): StoredRef | null {
  return read();
}

export function clearAffiliateRef(): void {
  if (!isBrowser()) return;
  localStorage.removeItem(STORAGE_KEY);
}

export const AFFILIATE_ATTRIBUTION_DAYS = 30;
